/**
 * RuleTemplateGallery — pre-built rule templates for quick creation.
 */

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: 'var(--red)',
  HIGH: '#e65100',
  MEDIUM: 'var(--amber)',
  LOW: 'var(--green)',
};

export interface RuleTemplate {
  name: string;
  description: string;
  category: string;
  exception_type: string;
  severity: string;
  conditions: any;
  severity_rules?: any[];
  output_template: { title: string; description_template?: string; recommended_action?: string };
}

const RULE_TEMPLATES: RuleTemplate[] = [
  {
    name: 'Missing PO over threshold',
    description: 'Flag AP invoices without a purchase order above a configurable amount',
    category: 'accounts_payable',
    exception_type: 'missing_po',
    severity: 'MEDIUM',
    conditions: {
      logic: 'AND',
      rules: [
        { field: 'document_number', operator: 'IS_NULL', value: '' },
        { field: 'amount', operator: 'GT', value: 10000 },
      ],
    },
    severity_rules: [
      { condition: { field: 'amount', operator: 'GT', value: 100000 }, severity: 'HIGH' },
    ],
    output_template: {
      title: 'Missing PO: {vendor_code} - ${amount}',
      description_template: 'Invoice {document_number} from {vendor_code} for ${amount} has no linked purchase order.',
      recommended_action: 'Verify with procurement whether a PO exists or obtain retroactive approval.',
    },
  },
  {
    name: 'Overdue AP invoices',
    description: 'Detect unpaid invoices past due date by configurable days',
    category: 'accounts_payable',
    exception_type: 'overdue_payable',
    severity: 'MEDIUM',
    conditions: {
      logic: 'AND',
      rules: [
        { field: 'aging_days', operator: 'GT', value: 30 },
        { field: 'amount', operator: 'GT', value: 0 },
      ],
    },
    severity_rules: [
      { condition: { field: 'aging_days', operator: 'GT', value: 90 }, severity: 'HIGH' },
    ],
    output_template: {
      title: 'Overdue AP: {vendor_code} - {aging_days} days',
      description_template: 'Invoice {document_number} is {aging_days} days past due.',
      recommended_action: 'Escalate to AP team for immediate payment or dispute resolution.',
    },
  },
  {
    name: 'Large round amount',
    description: 'Flag suspiciously round transaction amounts',
    category: 'general_ledger',
    exception_type: 'journal_anomaly',
    severity: 'LOW',
    conditions: {
      logic: 'AND',
      rules: [
        { field: 'amount', operator: 'GT', value: 50000 },
      ],
    },
    output_template: {
      title: 'Large amount: ${amount}',
      description_template: 'Transaction of ${amount} flagged for review.',
      recommended_action: 'Review supporting documentation and verify authorization.',
    },
  },
  {
    name: 'Duplicate payment detection',
    description: 'Find potential duplicate payments to same vendor',
    category: 'accounts_payable',
    exception_type: 'duplicate_payment',
    severity: 'HIGH',
    conditions: {
      logic: 'AND',
      rules: [
        { field: 'vendor_code', operator: 'IS_NOT_NULL', value: '' },
        { field: 'amount', operator: 'GT', value: 1000 },
      ],
    },
    output_template: {
      title: 'Potential duplicate: {vendor_code} - ${amount}',
      description_template: 'Multiple payments of ${amount} to {vendor_code} detected.',
      recommended_action: 'Compare invoice numbers and dates to confirm or rule out duplication.',
    },
  },
  {
    name: 'Intercompany imbalance',
    description: 'Flag intercompany transactions where offsetting entries do not match',
    category: 'intercompany',
    exception_type: 'ic_imbalance',
    severity: 'HIGH',
    conditions: {
      logic: 'AND',
      rules: [
        { field: 'account_type', operator: 'EQ', value: 'IC' },
        { field: 'amount', operator: 'GT', value: 5000 },
      ],
    },
    output_template: {
      title: 'IC imbalance: {entity_id} - ${amount}',
      description_template: 'Intercompany transaction of ${amount} has no matching offset.',
      recommended_action: 'Coordinate with counterparty entity to post matching entry.',
    },
  },
  {
    name: 'High-value journal entry',
    description: 'Flag manual journal entries above a threshold for review',
    category: 'general_ledger',
    exception_type: 'journal_anomaly',
    severity: 'MEDIUM',
    conditions: {
      logic: 'AND',
      rules: [
        { field: 'abs_amount', operator: 'GT', value: 100000 },
      ],
    },
    severity_rules: [
      { condition: { field: 'abs_amount', operator: 'GT', value: 500000 }, severity: 'CRITICAL' },
    ],
    output_template: {
      title: 'High-value JE: ${amount} by {posted_by}',
      description_template: 'Manual journal entry of ${amount} posted by {posted_by} requires review.',
      recommended_action: 'Verify authorization level and supporting documentation.',
    },
  },
];

interface RuleTemplateGalleryProps {
  onSelect: (template: RuleTemplate) => void;
  btnStyle: React.CSSProperties;
}

export function RuleTemplateGallery({ onSelect, btnStyle }: RuleTemplateGalleryProps) {
  return (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        Start from template
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
        {RULE_TEMPLATES.map((t, i) => (
          <div
            key={i}
            style={{
              border: '1px solid var(--border-default)',
              borderRadius: 8,
              padding: 12,
              cursor: 'pointer',
              transition: 'all 0.15s',
              background: 'var(--bg-card)',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent, var(--accent))'; e.currentTarget.style.background = 'var(--accent-subtle)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.background = 'var(--bg-card)'; }}
            onClick={() => onSelect(t)}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.4 }}>
              {t.description}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(0,0,0,0.06)', color: 'var(--text-secondary)' }}>
                {t.category.replace(/_/g, ' ')}
              </span>
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, fontWeight: 600, color: 'var(--text-primary)', background: SEVERITY_COLORS[t.severity] || 'var(--text-tertiary)' }}>
                {t.severity}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
        or configure from scratch below
      </div>
    </div>
  );
}
